import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { startOfWeek, format } from 'date-fns';
import confetti from 'canvas-confetti';

interface WeeklyChallenge {
  id: string;
  challenge_key: string;
  title: string;
  description: string;
  icon: string;
  challenge_type: string;
  target_value: number;
  target_unit: string;
  bonus_points: number;
}

interface ChallengeProgress {
  id: string;
  challenge_id: string;
  current_progress: number;
  is_completed: boolean;
  completed_at: string | null;
}

interface ChallengeWithProgress extends WeeklyChallenge {
  progress: ChallengeProgress | null;
}

interface WeeklyChallengesContextType {
  challenges: ChallengeWithProgress[];
  loading: boolean;
  updateChallengeProgress: (challengeType: string, incrementBy?: number) => Promise<void>;
  getProgressPercentage: (challenge: ChallengeWithProgress) => number;
  getCompletedCount: () => number;
  loadChallenges: () => Promise<void>;
}

const WeeklyChallengesContext = createContext<WeeklyChallengesContextType | null>(null);

export const useWeeklyChallengesContext = () => {
  const context = useContext(WeeklyChallengesContext);
  if (!context) {
    throw new Error('useWeeklyChallengesContext must be used within a WeeklyChallengesProvider');
  }
  return context;
};

export const WeeklyChallengesProvider = ({ children }: { children: React.ReactNode }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [challenges, setChallenges] = useState<ChallengeWithProgress[]>([]);
  const [loading, setLoading] = useState(true);

  const getWeekStart = useCallback(() => {
    return format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd');
  }, []);

  const loadChallenges = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const { data: challengesData, error: challengesError } = await supabase
        .from('weekly_challenges')
        .select('*')
        .eq('is_active', true);

      if (challengesError) throw challengesError;

      const weekStart = getWeekStart();

      const { data: progressData, error: progressError } = await supabase
        .from('user_challenge_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('week_start', weekStart);

      if (progressError) throw progressError;

      const challengesWithProgress: ChallengeWithProgress[] = (challengesData || []).map(challenge => {
        const progress = progressData?.find(p => p.challenge_id === challenge.id) || null;
        return { ...challenge, progress };
      });

      setChallenges(challengesWithProgress);
    } catch (error) {
      console.error('Error loading challenges:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, getWeekStart]);

  const initializeProgress = useCallback(async (challengeId: string) => {
    if (!userId) return null;

    const weekStart = getWeekStart();

    const { data, error } = await supabase
      .from('user_challenge_progress')
      .insert({
        user_id: userId,
        challenge_id: challengeId,
        week_start: weekStart,
        current_progress: 0
      })
      .select()
      .single();

    if (error && error.code !== '23505') {
      console.error('Error initializing progress:', error);
      return null;
    }

    // If unique constraint, fetch existing
    if (error?.code === '23505') {
      const { data: existing } = await supabase
        .from('user_challenge_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('challenge_id', challengeId)
        .eq('week_start', weekStart)
        .single();
      return existing;
    }

    return data;
  }, [userId, getWeekStart]);

  const updateChallengeProgress = useCallback(async (
    challengeType: string, 
    incrementBy: number = 1
  ) => {
    if (!userId) return;

    // Find challenges matching this type
    const matchingChallenges = challenges.filter(c => c.challenge_type === challengeType);
    if (matchingChallenges.length === 0) return;

    const weekStart = getWeekStart();

    for (const challenge of matchingChallenges) {
      let progress = challenge.progress;
      
      if (!progress) {
        progress = await initializeProgress(challenge.id);
        if (!progress) continue;
      }

      // Don't update if already completed
      if (progress.is_completed) continue;

      const newProgress = Math.min(progress.current_progress + incrementBy, challenge.target_value);
      const isNowCompleted = newProgress >= challenge.target_value;

      const updateData: Record<string, any> = {
        current_progress: newProgress
      };

      if (isNowCompleted) {
        updateData.is_completed = true;
        updateData.completed_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('user_challenge_progress')
        .update(updateData)
        .eq('user_id', userId)
        .eq('challenge_id', challenge.id)
        .eq('week_start', weekStart);

      if (error) {
        console.error('Error updating progress:', error);
        continue;
      }

      // Award bonus points if completed
      if (isNowCompleted) {
        confetti({
          particleCount: 80,
          spread: 60,
          origin: { y: 0.7 }
        });

        toast.success(`🎉 Desafio "${challenge.title}" completado! +${challenge.bonus_points} pontos bônus!`, {
          duration: 4000
        });
        
        await supabase.from('gamification_history').insert({
          user_id: userId,
          action_key: `challenge_${challenge.challenge_key}`,
          points_earned: challenge.bonus_points,
          description: `Desafio completado: ${challenge.title}`
        });

        const { data: gamData } = await supabase
          .from('user_gamification')
          .select('total_points')
          .eq('user_id', userId)
          .single();

        if (gamData) {
          await supabase
            .from('user_gamification')
            .update({ total_points: gamData.total_points + challenge.bonus_points })
            .eq('user_id', userId);
        }
      }
    }

    // Reload challenges to update UI
    await loadChallenges();
  }, [userId, challenges, getWeekStart, initializeProgress, loadChallenges]);

  const getProgressPercentage = useCallback((challenge: ChallengeWithProgress) => {
    if (!challenge.progress) return 0;
    return Math.round((challenge.progress.current_progress / challenge.target_value) * 100);
  }, []);

  const getCompletedCount = useCallback(() => {
    return challenges.filter(c => c.progress?.is_completed).length;
  }, [challenges]);

  useEffect(() => {
    const loadUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    loadUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUserId(session.user.id);
      } else {
        setUserId(null);
        setChallenges([]);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (userId) {
      loadChallenges();
    }
  }, [userId, loadChallenges]);

  return (
    <WeeklyChallengesContext.Provider value={{
      challenges,
      loading,
      updateChallengeProgress,
      getProgressPercentage,
      getCompletedCount,
      loadChallenges
    }}>
      {children}
    </WeeklyChallengesContext.Provider>
  );
};
